import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { api } from "@/api";
import { inputCls } from "@/admin/ResourceTab";

const COMPANY_FIELDS = [
  ["legalName", "Legal Name"], ["shortName", "Short Name"], ["cin", "CIN"],
  ["incorporationDate", "Incorporation Date"], ["companyType", "Company Type"],
  ["companyCategory", "Company Category"], ["subCategory", "Sub-category"],
  ["roc", "Registrar of Companies"], ["status", "Corporate Status"],
  ["registeredState", "Registered State"], ["country", "Country"],
  ["corporateLocation", "Corporate Location"], ["email", "Corporate Email"], ["phone", "Corporate Phone"],
];

const COMPANY_AREAS = [
  ["registeredOffice", "Registered Office (full address)"],
  ["positioning", "Corporate Positioning"],
  ["tagline", "Tagline"],
  ["vision", "Vision"],
  ["mission", "Mission"],
  ["about", "About / Who We Are"],
];

export function CompanyTab() {
  const [company, setCompany] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/company").then((r) => setCompany(r.data)).catch(() => toast.error("Failed to load company"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!company) return <p className="text-sm text-slate-500">Loading…</p>;
  const set = (k) => (e) => setCompany((c) => ({ ...c, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/company", company);
      toast.success("Company information saved");
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-company">
      <h2 className="font-serif text-2xl text-navy-900">Company Information</h2>
      <p className="mt-1 text-sm text-slate-500">Legal and corporate details shown across the website.</p>
      <div className="mt-6 grid gap-4 rounded-xl border border-slate-200 bg-white p-6 sm:grid-cols-2 lg:grid-cols-3">
        {COMPANY_FIELDS.map(([k, label]) => (
          <div key={k}>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</label>
            <input className={inputCls} value={company[k] ?? ""} onChange={set(k)} data-testid={`company-${k}`} />
          </div>
        ))}
        {COMPANY_AREAS.map(([k, label]) => (
          <div key={k} className="sm:col-span-2 lg:col-span-3">
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</label>
            <textarea rows={k === "about" ? 4 : 2} className={inputCls} value={company[k] ?? ""} onChange={set(k)} data-testid={`company-${k}`} />
          </div>
        ))}
      </div>
      <button onClick={save} disabled={saving} className="btn-gold mt-6 !px-6 !py-2.5 !text-xs disabled:opacity-60" data-testid="company-save">
        {saving ? "Saving…" : "Save Company Information"}
      </button>
    </div>
  );
}

function PairsEditor({ label, pairs, onChange, testid }) {
  useEffect(() => {
    if (pairs.some((p) => !p._k)) {
      onChange(pairs.map((p) => (p._k ? p : { ...p, _k: crypto.randomUUID() })));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const update = (i, key, val) => onChange(pairs.map((p, j) => (j === i ? { ...p, [key]: val } : p)));
  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</label>
        <button
          type="button"
          onClick={() => onChange([...pairs, { value: "", label: "", _k: crypto.randomUUID() }])}
          className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-2.5 py-1 text-xs font-semibold text-slate-600 hover:border-gold-dark hover:text-gold-dark"
          data-testid={`${testid}-add`}
        >
          <Plus className="h-3 w-3" /> Add
        </button>
      </div>
      <div className="space-y-2">
        {pairs.map((p, i) => (
          <div key={p._k || i} className="flex gap-2">
            <input className={inputCls} placeholder="Value (e.g. 2024)" value={p.value} onChange={(e) => update(i, "value", e.target.value)} data-testid={`${testid}-value-${i}`} />
            <input className={inputCls} placeholder="Label (e.g. Established)" value={p.label} onChange={(e) => update(i, "label", e.target.value)} data-testid={`${testid}-label-${i}`} />
            <button
              type="button"
              onClick={() => onChange(pairs.filter((_, j) => j !== i))}
              aria-label="Remove"
              className="rounded-md border border-slate-200 p-2 text-slate-500 hover:border-red-400 hover:text-red-500"
              data-testid={`${testid}-remove-${i}`}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

export function SettingsTab() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => toast.error("Failed to load settings"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!settings) return <p className="text-sm text-slate-500">Loading…</p>;

  const setSocial = (k) => (e) => setSettings((s) => ({ ...s, socials: { ...s.socials, [k]: e.target.value } }));

  const save = async () => {
    setSaving(true);
    try {
      const clean = (pairs) => (pairs || []).map(({ value, label }) => ({ value, label }));
      await api.put("/settings", { ...settings, stats: clean(settings.stats), impactMetrics: clean(settings.impactMetrics) });
      toast.success("Settings saved");
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-settings">
      <h2 className="font-serif text-2xl text-navy-900">Site Settings</h2>
      <p className="mt-1 text-sm text-slate-500">Homepage statistics, impact metrics, social links and analytics IDs.</p>
      <div className="mt-6 space-y-8 rounded-xl border border-slate-200 bg-white p-6">
        <PairsEditor
          label="Company at a Glance — Statistics"
          pairs={settings.stats || []}
          onChange={(stats) => setSettings((s) => ({ ...s, stats }))}
          testid="settings-stats"
        />
        <PairsEditor
          label="Impact Metrics (publish only verified numbers)"
          pairs={settings.impactMetrics || []}
          onChange={(impactMetrics) => setSettings((s) => ({ ...s, impactMetrics }))}
          testid="settings-impact"
        />
        <div>
          <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-slate-500">Social Links (icons appear only when filled)</label>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {["linkedin", "instagram", "facebook", "youtube", "x", "whatsapp"].map((k) => (
              <input
                key={k}
                className={inputCls}
                placeholder={`${k.charAt(0).toUpperCase() + k.slice(1)} URL`}
                value={settings.socials?.[k] ?? ""}
                onChange={setSocial(k)}
                data-testid={`settings-social-${k}`}
              />
            ))}
          </div>
        </div>
        <div>
          <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-slate-500">Analytics (leave empty to disable)</label>
          <div className="grid gap-3 sm:grid-cols-2">
            <input className={inputCls} placeholder="Google Analytics 4 ID (G-…)" value={settings.gaId ?? ""} onChange={(e) => setSettings((s) => ({ ...s, gaId: e.target.value }))} data-testid="settings-ga" />
            <input className={inputCls} placeholder="Google Tag Manager ID (GTM-…)" value={settings.gtmId ?? ""} onChange={(e) => setSettings((s) => ({ ...s, gtmId: e.target.value }))} data-testid="settings-gtm" />
          </div>
        </div>
      </div>
      <button onClick={save} disabled={saving} className="btn-gold mt-6 !px-6 !py-2.5 !text-xs disabled:opacity-60" data-testid="settings-save">
        {saving ? "Saving…" : "Save Settings"}
      </button>
    </div>
  );
}
